import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from '../../../src/i18n';
import { TP, dateFormats } from '../../../constants/consts';
import CardBox from '../../CardBox';
import TitledBlock from '../../TitledBlock';
import * as languageIcons from '../../../public/images/flags';
import EditCardButtons from '../EditCardButtons';
import { getOption } from '../../../utils/common';
import { createDate } from '../../../utils/date';
import ImageTag from '../../ImageTag';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  cardSection: {
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'center',
      textAlign: 'center',
    },
  },
  bioTitle: {
    [theme.breakpoints.down('xs')]: {
      marginRight: theme.spacing(2),
      width: 'auto',
    },
  },
  time: {
    fontWeight: 'bold',
  },
  langName: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    textTransform: 'uppercase',
  },
  buttonsBlock: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  editButton: {
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginBottom: 0,
    },
  },
}));

const EditTranslationCard = ({
  translation,
  editTranslationClick,
  updatePrivacy,
  privacyOptions,
  deleteFunc,
  editButtonLabel,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const langFlag = languageIcons[`lang_${translation?.language?.lang}`];
  const visibility = translation?.privacyAttribute?.id ? getOption(translation?.privacyAttribute) : null;

  const onBioDelete = () => {
    deleteFunc(translation?.id);
  };

  return (
    <CardBox className={classes.root}>
      <div className={classes.cardSection}>
        <TitledBlock title={t(`${TP}.m_LANG`)}>
          {langFlag && (
            <ImageTag
              src={languageIcons[`lang_${translation?.language?.lang}`]}
              alt={`${translation?.language?.lang} flag`}
            />
          )}
          <span className={classes.langName}>{translation?.language?.lang}</span>
        </TitledBlock>
        <div className={classes.cardSection}>
          <TitledBlock title={t(`${TP}.FN_EDITARTIST_CREATED_DATE`)} className={classes.bioTitle}>
            <time className={classes.time} dateTime={translation?.createdAt}>
              {createDate(translation?.createdAt).format(dateFormats.shortMonthEvent)}
            </time>
          </TitledBlock>
          <TitledBlock title={t(`${TP}.m_LASTUPDATE`)} className={classes.bioTitle}>
            <time className={classes.time} dateTime={translation?.updatedAt}>
              {createDate(translation?.updatedAt).format(dateFormats.shortMonthEvent)}
            </time>
          </TitledBlock>
        </div>
      </div>
      <EditCardButtons
        editClick={editTranslationClick}
        visibilityClick={updatePrivacy ? v => updatePrivacy(v?.value) : undefined}
        editButtonName={editButtonLabel}
        privacyOptions={privacyOptions}
        value={visibility}
        onDelete={deleteFunc ? onBioDelete : undefined}
      />
    </CardBox>
  );
};

EditTranslationCard.propTypes = {
  translation: PropTypes.object.isRequired,
  editTranslationClick: PropTypes.func.isRequired,
  updatePrivacy: PropTypes.func,
  deleteFunc: PropTypes.func,
  editButtonLabel: PropTypes.string,
};

export default memo(EditTranslationCard);
